import React, { useCallback, useContext, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTypes, Button, Drawer, Icon, useDrawer } from '@/components/shared';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import { AccountId, FilterUnitRegistrationResponse, RegistrationCompositeId } from '@/generated/data.schemas';
import { getGetFilterRegistrationQueryKey } from '@/generated/filter-registration';
import { getGetRegistrationCompositeQueryKey } from '@/generated/registration-composite';
import { useCreateSparePartRegistration } from '@/generated/spare-parts';
import { useGetTranslations } from '@/generated/translations';
import { isHttpValidationProblemDetails } from '@/helpers';
import { useNotificationStore } from '@/store/notificationStore';
import styles from './SparePart.module.scss';
import { SparePartForm } from './shared/SparePartForm';

interface RegisterSparePartProps {
	ariaControlId: string;
	accountId: AccountId;
	registrationCompositeId: RegistrationCompositeId;
	filterUnit: FilterUnitRegistrationResponse;
}

export const RegisterSparePart: React.FunctionComponent<RegisterSparePartProps> = ({
	ariaControlId,
	accountId,
	registrationCompositeId,
	filterUnit,
}) => {
	const { segmentationId } = useContext(SegmentationContext);
	const { data: translations } = useGetTranslations(segmentationId);

	const addNotification = useNotificationStore((state) => state.addNotification);
	const queryClient = useQueryClient();

	const [validationErrors, setValidationErrors] = useState<Record<string, string[]> | null>(null);
	const [hasFormChanged, setHasFormChanged] = useState(false);

	const formId = 'register-sparePart-form'; // used for binding form and submit button
	const drawerRef = useRef<HTMLDivElement>(null);
	const { isOpen, isRendered, handleOpen, handleClose } = useDrawer(drawerRef);

	const createSparePartRegistrationMutation = useCreateSparePartRegistration({
		mutation: {
			onSuccess: async () => {
				await queryClient.invalidateQueries({
					queryKey: getGetRegistrationCompositeQueryKey(segmentationId, accountId, registrationCompositeId),
				});

				// refresh filter unit after update
				await queryClient.invalidateQueries({
					queryKey: getGetFilterRegistrationQueryKey(
						segmentationId,
						accountId,
						registrationCompositeId,
						filterUnit?.id,
					),
				});

				addNotification({
					children: translations?.data.sparePartRegistrations.saveSuccess,
					type: AlertTypes.SUCCESS,
				});

				handleClose();
			},
			onError: (axiosError) => {
				const error = axiosError.response?.data;
				if (!error) {
					console.error('Unknown error', axiosError);
				} else if (isHttpValidationProblemDetails(error)) {
					addNotification({
						children: translations?.data.sparePartRegistrations.saveFailed,
						type: AlertTypes.DANGER,
					});

					setValidationErrors(error.errors);
				} else {
					addNotification({
						children: translations?.data.shared.genericErrorMsg,
						type: AlertTypes.DANGER,
					});
				}
			},
		},
	});

	const openDrawer = useCallback((): void => {
		setHasFormChanged(false);
		handleOpen();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// set has input text changed state - used for disabling the save button
	const handleChange = (): void => {
		if (hasFormChanged) return;
		setHasFormChanged(true);
	};

	const handleSubmit = (event) => {
		event.preventDefault();
		const formData = new FormData(event.target);

		setValidationErrors(null);

		const sparePartForm = {
			articleNumber: String(formData.get('articleNumber')),
			quantity: Number(formData.get('quantity')) || 1,
			saleDate: formData.get('saleDate') ? String(formData.get('saleDate')) : null,
		};

		createSparePartRegistrationMutation.mutate({
			segmentationId,
			accountId,
			registrationCompositeId,
			filterUnitId: filterUnit.id,
			data: sparePartForm,
		});

		event.target.reset();

		handleClose();
	};

	return (
		<>
			{/* Trigger button */}

			{filterUnit.spareParts.length ? (
				<Button
					aria-label={translations?.data.sparePartRegistrations.addSparePart}
					variant={'ghost'}
					onClick={openDrawer}
					aria-expanded={isOpen}
					aria-controls={ariaControlId}
				>
					<Icon name={'plus'} size={'sm'} />
				</Button>
			) : (
				<Button isFullWidth onClick={openDrawer} aria-expanded={isOpen} aria-controls={ariaControlId}>
					{translations?.data.sparePartRegistrations.addSparePart}
					<Icon name={'plus'} size={'sm'} />
				</Button>
			)}

			{isRendered && (
				<Drawer
					drawerRef={drawerRef}
					isOpen={isOpen}
					id={ariaControlId}
					desktopWidth={'400px'}
					direction={'Right'}
				>
					<Drawer.Header
						title={translations?.data.sparePartRegistrations.addSparePart}
						handleClose={handleClose}
					/>

					<Drawer.Body>
						<div className={styles.innerBody}>
							<SparePartForm
								formId={formId}
								legend={translations?.data.sparePartRegistrations.addSparePart}
								validationErrors={validationErrors}
								handleSubmit={handleSubmit}
								handleChange={handleChange}
							/>
						</div>
					</Drawer.Body>

					<Drawer.Footer className={styles.footer} hasNoPadding>
						<Button variant={'ghost'} isFullWidth onClick={handleClose}>
							{translations?.data.shared.cancel}
						</Button>
						<Button type="submit" form={formId} variant={'primary'} isFullWidth disabled={!hasFormChanged}>
							{translations?.data.sparePartRegistrations.save}
						</Button>
					</Drawer.Footer>
				</Drawer>
			)}
		</>
	);
};
